import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./auth/auth.css";
import { AUTH_BASE_URL } from "./config";

const SignupPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmationTouched, setConfirmationTouched] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState(null);

  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const showConfirmation = confirmationTouched || confirmPassword.length > 0;
  const mismatch = showConfirmation && !passwordsMatch;
  const canSubmit = Boolean(email.trim() && password.trim() && passwordsMatch);

  const handleSignup = async (event) => {
    event.preventDefault();
    if (submitting || created) return;
    setError(null);
    if (!canSubmit) {
      setConfirmationTouched(true);
      setError("Enter your email and make sure both passwords match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${AUTH_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) throw new Error("Signup failed");
      setCreated(true);
      setPassword("");
      setConfirmPassword("");
    } catch {
      setError("We couldn't create your account. Please try again. If you already have an account, log in instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page signup-page">
      <section className="auth-card signup-card" aria-labelledby="signup-title">
        <div className="signup-intro">
          <Link className="signup-brand" to="/">Signal Fly</Link>
          <span className="signup-eyebrow">NEW TO SIGNALFLY</span>
          <h1 id="signup-title">Create your account</h1>
          <p>Set up your login to explore your SignalFly workspace.</p>
          <div className="signup-approval-note">
            New accounts require admin approval before you can log in.
          </div>
          <p className="signup-existing">Already registered? <Link to="/login">Log in to your account</Link></p>
        </div>

        <div className="signup-form-panel">
          {created ? (
            <div className="signup-complete">
              <h2>You're registered</h2>
              <div className="auth-success" role="status">
                Account created successfully. Your account is pending admin approval.
              </div>
              <p>You can log in once your account has been approved.</p>
              <Link className="auth-btn signup-login-link" to="/login">Continue to login</Link>
            </div>
          ) : (
            <form className="signup-form" onSubmit={handleSignup} aria-label="Create account" aria-busy={submitting}>
              <h2>Your login details</h2>
              <p className="signup-form-description">Choose a password, then re-enter it to confirm.</p>
              {error && <div className="auth-error" role="alert">{error}</div>}
              <label htmlFor="signup-email">Email address</label>
              <input id="signup-email" name="email" className="auth-input" type="email"
                autoComplete="email" placeholder="you@example.com" required value={email}
                disabled={submitting} onChange={event => setEmail(event.target.value)} />
              <label htmlFor="signup-password">Password</label>
              <input id="signup-password" name="password" className="auth-input"
                type={showPasswords ? "text" : "password"} autoComplete="new-password" required
                value={password} disabled={submitting} onChange={event => setPassword(event.target.value)} />
              <label htmlFor="signup-confirm-password">Confirm password</label>
              <input id="signup-confirm-password" name="confirmPassword" className="auth-input"
                type={showPasswords ? "text" : "password"} autoComplete="new-password" required
                value={confirmPassword} disabled={submitting}
                aria-invalid={mismatch} aria-describedby="signup-password-feedback"
                onBlur={() => setConfirmationTouched(true)}
                onChange={event => setConfirmPassword(event.target.value)} />
              <div id="signup-password-feedback" aria-live="polite"
                className={`signup-password-feedback ${passwordsMatch ? "is-match" : "is-mismatch"}`}>
                {showConfirmation ? (passwordsMatch ? "Passwords match." : "Passwords do not match.") : ""}
              </div>
              <button className="signup-password-toggle" type="button" aria-pressed={showPasswords}
                disabled={submitting} onClick={() => setShowPasswords(value => !value)}>
                {showPasswords ? "Hide passwords" : "Show passwords"}
              </button>
              <button className="auth-btn" type="submit" disabled={!canSubmit || submitting}>
                {submitting ? "Creating account..." : "Create account"}
              </button>
              <Link className="signup-home-link" to="/">Back to home</Link>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default SignupPage;
